import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Radio,
  message,
  Descriptions,
  Image,
  Pagination,
  Select,
  Typography,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  BugOutlined,
  MessageOutlined,
  PaperClipOutlined,
  PictureOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { userFeedbackApi, taskConfigApi } from '@/api/client';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;
const { Text, Paragraph } = Typography;

interface FeedbackItem {
  id: number;
  user_id: number;
  user_email: string;
  user_nickname: string;
  feedback_type: 'demand' | 'bug';
  title: string;
  content: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_reply?: string;
  reward_credits: number;
  reviewed_by?: number;
  reviewer_name?: string;
  reviewed_at?: string;
  created_at: string;
  attachment_count: number;
  attachments?: Array<{
    id: number;
    file_type: 'image' | 'document';
    file_name: string;
    file_url: string;
    mime_type: string;
  }>;
}

const statusMap = {
  pending: { color: 'warning', text: '待审核' },
  approved: { color: 'success', text: '已通过' },
  rejected: { color: 'error', text: '已拒绝' },
};

const typeMap = {
  demand: { text: '需求', icon: <FileTextOutlined />, color: 'blue' },
  bug: { text: '问题', icon: <BugOutlined />, color: 'red' },
};

export default function FeedbackPage() {
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [titleFilter, setTitleFilter] = useState<string>('');
  const [searchTrigger, setSearchTrigger] = useState<number>(0);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState<FeedbackItem | null>(null);
  const [form] = Form.useForm();

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await userFeedbackApi.getAdminFeedbacks({
        page,
        pageSize,
        status: statusFilter || undefined,
        feedbackType: typeFilter || undefined,
        user: userFilter || undefined,
        title: titleFilter || undefined,
      });
      // 后端返回格式：{ success: true, data: { list, pagination } }
      setFeedbacks(res.data.data.list || []);
      setTotal(res.data.data.pagination?.total || 0);
    } catch (error) {
      message.error('获取反馈列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [page, statusFilter, typeFilter, searchTrigger]);

  const handleViewDetail = async (record: FeedbackItem) => {
    try {
      const res = await userFeedbackApi.getAdminFeedbackDetail(record.id);
      setCurrentFeedback(res.data);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('获取反馈详情失败');
    }
  };

  const handleReview = async (record: FeedbackItem) => {
    setCurrentFeedback(record);
    form.resetFields();
    
    // 获取任务配置，设置默认积分
    try {
      const res = await taskConfigApi.getTaskConfigs();
      const taskConfigs = res.data.data || [];
      const taskType = record.feedback_type === 'demand' ? 'feedback_demand' : 'feedback_bug';
      const config = taskConfigs.find((c: any) => c.task_type === taskType);
      const defaultCredits = config?.reward_credits || (record.feedback_type === 'demand' ? 100 : 50);
      
      form.setFieldsValue({
        status: 'approved',
        rewardCredits: defaultCredits,
      });
    } catch (error) {
      // 如果获取配置失败，使用默认值
      const defaultCredits = record.feedback_type === 'demand' ? 100 : 50;
      form.setFieldsValue({
        status: 'approved',
        rewardCredits: defaultCredits,
      });
    }
    
    setReviewModalVisible(true);
  };

  const handleReviewSubmit = async (values: any) => {
    if (!currentFeedback) return;

    try {
      const res = await userFeedbackApi.reviewFeedback(currentFeedback.id, {
        status: values.status,
        adminReply: values.adminReply,
        rewardCredits: values.rewardCredits ? Number(values.rewardCredits) : undefined,
      });

      message.success(res.data.message);
      setReviewModalVisible(false);
      fetchFeedbacks();
    } catch (error: any) {
      message.error(error.response?.data?.error || '审核失败');
    }
  };

  const columns: any[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
      fixed: 'left' as const,
    },
    {
      title: '类型',
      dataIndex: 'feedback_type',
      width: 80,
      render: (type: 'demand' | 'bug') => (
        <Tag color={typeMap[type].color} icon={typeMap[type].icon}>
          {typeMap[type].text}
        </Tag>
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      width: 200,
      ellipsis: true,
    },
    {
      title: '用户',
      dataIndex: 'user_email',
      width: 160,
      ellipsis: true,
      render: (email: string, record: FeedbackItem) => (
        <div>
          <div style={{ fontSize: 13 }}>{record.user_nickname || email}</div>
          {record.user_nickname && <Text type="secondary" style={{ fontSize: 11 }}>{email}</Text>}
        </div>
      ),
    },
    {
      title: '附件',
      dataIndex: 'attachment_count',
      width: 70,
      render: (count: number) => count > 0 ? <Tag>{count}</Tag> : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status: 'pending' | 'approved' | 'rejected') => (
        <Tag color={statusMap[status].color}>{statusMap[status].text}</Tag>
      ),
    },
    {
      title: '积分',
      dataIndex: 'reward_credits',
      width: 70,
      render: (credits: number, record: FeedbackItem) =>
        record.status === 'approved' && credits > 0 ? (
          <Tag color="green">+{credits}</Tag>
        ) : '-',
    },
    {
      title: '提交时间',
      dataIndex: 'created_at',
      width: 140,
      render: (date: string) => dayjs(date).format('MM-DD HH:mm'),
    },
    {
      title: '操作',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: FeedbackItem) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            查看
          </Button>
          {record.status === 'pending' && (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleReview(record)}
            >
              审核
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="用户反馈管理"
        extra={
          <Space>
            <Input
              placeholder="搜索用户（邮箱/昵称）"
              allowClear
              style={{ width: 180 }}
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              onPressEnter={() => { setPage(1); setSearchTrigger(prev => prev + 1); }}
            />
            <Input
              placeholder="搜索标题"
              allowClear
              style={{ width: 180 }}
              value={titleFilter}
              onChange={(e) => setTitleFilter(e.target.value)}
              onPressEnter={() => { setPage(1); setSearchTrigger(prev => prev + 1); }}
            />
            <Select
              placeholder="筛选状态"
              allowClear
              style={{ width: 120 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
            <Select
              placeholder="筛选类型"
              allowClear
              style={{ width: 120 }}
              value={typeFilter}
              onChange={setTypeFilter}
            >
              <Option value="demand">产品需求</Option>
              <Option value="bug">问题反馈</Option>
            </Select>
            <Button type="primary" onClick={() => { setPage(1); setSearchTrigger(prev => prev + 1); }}>
              查询
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={feedbacks}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="反馈详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
          currentFeedback?.status === 'pending' && (
            <Button
              key="review"
              type="primary"
              onClick={() => {
                setDetailModalVisible(false);
                handleReview(currentFeedback);
              }}
            >
              去审核
            </Button>
          ),
        ]}
        width={720}
        maskClosable={false}
      >
        {currentFeedback && (
          <>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="反馈ID">{currentFeedback.id}</Descriptions.Item>
              <Descriptions.Item label="反馈类型">
                <Tag color={typeMap[currentFeedback.feedback_type].color}>
                  {typeMap[currentFeedback.feedback_type].text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="用户邮箱">{currentFeedback.user_email}</Descriptions.Item>
              <Descriptions.Item label="用户昵称">{currentFeedback.user_nickname || '-'}</Descriptions.Item>
              <Descriptions.Item label="提交时间">
                {dayjs(currentFeedback.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="审核状态">
                <Tag color={statusMap[currentFeedback.status].color}>
                  {statusMap[currentFeedback.status].text}
                </Tag>
              </Descriptions.Item>
              {currentFeedback.status !== 'pending' && (
                <>
                  <Descriptions.Item label="审核人">
                    {currentFeedback.reviewer_name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="审核时间">
                    {currentFeedback.reviewed_at
                      ? dayjs(currentFeedback.reviewed_at).format('YYYY-MM-DD HH:mm:ss')
                      : '-'}
                  </Descriptions.Item>
                </>
              )}
              {currentFeedback.reward_credits > 0 && (
                <Descriptions.Item label="奖励积分" span={2}>
                  <Tag color="green">+{currentFeedback.reward_credits} 积分</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16 }}>{currentFeedback.title}</Text>
            </div>

            <Descriptions.Item label="反馈内容" span={2}>
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 300,
                  overflow: 'auto',
                  padding: 12,
                  background: '#fafafa',
                  borderRadius: 8,
                  border: '1px solid #f0f0f0',
                  lineHeight: 1.8,
                }}
              >
                {currentFeedback.content || '-'}
              </div>
            </Descriptions.Item>

            {currentFeedback.attachments && currentFeedback.attachments.length > 0 && (
              <>
                <Divider />
                {/* 图片附件区域 */}
                {currentFeedback.attachments.filter(att =>
                  att.file_type === 'image' ||
                  /^image\//.test(att.mime_type || '') ||
                  /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(att.file_name)
                ).length > 0 && (
                  <>
                    <Divider orientation="left" plain>
                      <Text type="secondary">
                        图片（{currentFeedback.attachments.filter(att =>
                          att.file_type === 'image' ||
                          /^image\//.test(att.mime_type || '') ||
                          /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(att.file_name)
                        ).length}）
                      </Text>
                    </Divider>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {currentFeedback.attachments
                        .filter(att =>
                          att.file_type === 'image' ||
                          /^image\//.test(att.mime_type || '') ||
                          /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(att.file_name)
                        )
                        .map(attachment => (
                          <div key={attachment.id} style={{ display: 'inline-block', margin: '0 8px 8px 0', textAlign: 'center' }}>
                            <Image
                              src={attachment.file_url}
                              alt={attachment.file_name}
                              width={120}
                              height={120}
                              style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0', cursor: 'pointer' }}
                              preview={{ src: attachment.file_url }}
                            />
                            <div style={{ fontSize: 11, color: '#999', marginTop: 4, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {attachment.file_name}
                            </div>
                            <Button
                              type="link"
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = attachment.file_url;
                                link.download = attachment.file_name;
                                link.target = '_blank';
                                link.rel = 'noopener noreferrer';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              下载
                            </Button>
                          </div>
                        ))}
                    </div>
                  </>
                )}

                {/* 文档附件区域 */}
                {currentFeedback.attachments.filter(att =>
                  !(att.file_type === 'image' ||
                    /^image\//.test(att.mime_type || '') ||
                    /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(att.file_name))
                ).length > 0 && (
                  <>
                    <Divider orientation="left" plain>
                      <Text type="secondary">
                        文档（{currentFeedback.attachments.filter(att =>
                          !(att.file_type === 'image' ||
                            /^image\//.test(att.mime_type || '') ||
                            /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(att.file_name))
                        ).length}）
                      </Text>
                    </Divider>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {currentFeedback.attachments
                        .filter(att =>
                          !(att.file_type === 'image' ||
                            /^image\//.test(att.mime_type || '') ||
                            /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(att.file_name))
                        )
                        .map(attachment => (
                          <div
                            key={attachment.id}
                            style={{ display: 'inline-flex', alignItems: 'center', margin: '0 8px 8px 0', padding: '8px 12px', background: '#f5f5f5', borderRadius: 8, border: '1px solid #e8e8e8' }}
                          >
                            <PaperClipOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                              {attachment.file_name}
                            </span>
                            <Button
                              type="link"
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = attachment.file_url;
                                link.download = attachment.file_name;
                                link.target = '_blank';
                                link.rel = 'noopener noreferrer';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              下载
                            </Button>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </>
            )}

            {currentFeedback.admin_reply && (
              <>
                <Divider />
                <div style={{ background: '#f6ffed', padding: 16, borderRadius: 8 }}>
                  <Space style={{ marginBottom: 8 }}>
                    <MessageOutlined />
                    <Text strong>管理员回复</Text>
                  </Space>
                  <Paragraph style={{ marginBottom: 0 }}>{currentFeedback.admin_reply}</Paragraph>
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* 审核弹窗 */}
      <Modal
        title="审核反馈"
        open={reviewModalVisible}
        onCancel={() => setReviewModalVisible(false)}
        onOk={() => form.submit()}
        okText="确认审核"
        maskClosable={false}
      >
        {currentFeedback && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleReviewSubmit}
            initialValues={{ status: 'approved' }}
          >
            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <Text strong>{currentFeedback.title}</Text>
              <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0, marginTop: 8 }}>
                {currentFeedback.content}
              </Paragraph>
            </div>

            <Form.Item
              name="status"
              label="审核结果"
              rules={[{ required: true, message: '请选择审核结果' }]}
            >
              <Radio.Group>
                <Radio.Button value="approved">
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                  通过
                </Radio.Button>
                <Radio.Button value="rejected">
                  <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                  拒绝
                </Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.status !== curr.status}
            >
              {({ getFieldValue }) =>
                getFieldValue('status') === 'approved' ? (
                  <Form.Item
                    name="rewardCredits"
                    label="奖励积分"
                    extra="默认使用任务配置中的积分奖励值，可手动调整"
                  >
                    <Input type="number" min={0} placeholder="留空使用默认配置" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="adminReply"
              label="回复内容"
              rules={[{ max: 500, message: '回复内容最多500字' }]}
            >
              <TextArea
                rows={4}
                placeholder="可选：输入对用户的回复内容"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
